import React, { useMemo, useCallback } from "react"

// External libraries
import {
	Progress,
	Select,
	Input,
	Tag,
	notification,
	Checkbox,
	Tooltip,
} from "antd"
import {
	CheckCircleFilled,
	CloseCircleFilled,
	MinusCircleFilled,
} from "@ant-design/icons"

// Internal components
import { Table } from "components"

// Utilities
import { renderVehicleTypeIcon, WarehouseIcon } from "utils/appIcons"
import { getStatusColor, getStatusLabel } from "utils/status"
import Strings from "utils/strings"

// Types
import {
	NotDeliveredReason,
	Route,
	RouteStatus,
	ExternalEligibilityStatus,
	RouteExternalEligibility,
} from "../types"
import { ROUTE_STATUS_VALUES } from "../types"
import { isRouteEligibleForMoveOrders } from "./MoveOrdersTimeline/utils"
import RouteOptionsButton from "./RouteOptionsButton"

const { Option } = Select
const { Search } = Input

interface RoutesTableProps {
	routes: Route[]
	selectedRouteIds: string[]
	setSelectedRouteIds: (ids: string[]) => void
	handleRowPress: (route: Route, event?: React.MouseEvent) => void
	handleSearch: (search: string) => void
	selectedStatus: RouteStatus[]
	handleStatusChange: (status: RouteStatus[]) => void
	handleStatusBlur: () => void
	handleStatusClear: () => void
	fetchData: () => void
	pagination: {
		page: number
		pageSize: number
		total: number
	}
	onPageChange: (page: number, pageSize: number) => void
	notDeliveredReasons: NotDeliveredReason[]
	externalDeliveryActive: boolean
	showPanel: boolean
	onTogglePanel: () => void
	showTimeline: boolean
	onToggleTimeline: () => void
}

const RoutesTable: React.FC<RoutesTableProps> = ({
	routes,
	selectedRouteIds,
	setSelectedRouteIds,
	handleRowPress,
	handleSearch,
	selectedStatus,
	handleStatusChange,
	handleStatusBlur,
	handleStatusClear,
	fetchData,
	pagination,
	onPageChange,
	notDeliveredReasons,
	externalDeliveryActive,
	showPanel,
	onTogglePanel,
	showTimeline,
	onToggleTimeline,
}) => {
	const hasIneligibleSelectedRoute = useMemo(
		() =>
			routes.some(
				(route) =>
					selectedRouteIds.includes(route._id) &&
					!isRouteEligibleForMoveOrders(route)
			),
		[routes, selectedRouteIds]
	)
	const timelineDisabled =
		selectedRouteIds.length < 1 || hasIneligibleSelectedRoute

	// Memoized row style function
	const getRowStyle = useCallback(
		(row: any) => {
			const rowId = row.original?._id || row._id
			return selectedRouteIds.includes(rowId)
				? {
						backgroundColor: "var(--background-selected)", // Using CSS variable directly
						fontWeight: 500,
					}
				: {}
		},
		[selectedRouteIds]
	)

	// Memoized components
	const statusFilter = useMemo(() => {
		return (
			<Select
				mode="multiple"
				placeholder={Strings.routes.filterByStatus}
				value={selectedStatus}
				onChange={handleStatusChange}
				onDropdownVisibleChange={(open) => {
					// Fetch when dropdown closes
					if (!open) {
						handleStatusBlur()
					}
				}}
				onClear={handleStatusClear}
				allowClear
				style={{ width: 230 }}
				maxTagCount="responsive"
			>
				{ROUTE_STATUS_VALUES.map((status) => (
					<Option key={status} value={status}>
						{getStatusLabel(status)}
					</Option>
				))}
			</Select>
		)
	}, [selectedStatus, handleStatusChange, handleStatusBlur, handleStatusClear])

	const searchComponent = useMemo(
		() => (
			<Search
				placeholder={Strings.routes.searchRoutes}
				onSearch={handleSearch}
				onBlur={(e) => handleSearch(e.target.value)}
				style={{ width: 300 }}
				allowClear
				maxLength={80}
			/>
		),
		[handleSearch]
	)

	const routeOptionsButton = useMemo(
		() => <RouteOptionsButton onSyncSuccess={fetchData} />,
		[fetchData]
	)

	// Memoized header options
	const headerOptions = useMemo(
		() => [
			{
				type: "custom" as const,
				icon: "filter",
				label: "status_filter",
				customComponent: statusFilter,
			},
			{
				type: "custom" as const,
				icon: "settings",
				label: "route_options",
				customComponent: routeOptionsButton,
				marginRightAuto: true,
			},
			{
				icon: "list1",
				tooltip: showPanel
					? Strings.routes.hidePanel
					: Strings.routes.showPanel,
				noText: true,
				active: showPanel,
				onClick: onTogglePanel,
			},
			{
				icon: "route",
				tooltip:
					selectedRouteIds.length < 1
						? Strings.routes.timelineRequiresARoute
						: hasIneligibleSelectedRoute
							? Strings.routes.timelineRequiresEligibleRoutes
							: showTimeline
								? Strings.routes.hideTimeline
								: Strings.routes.showTimeline,
				noText: true,
				// Reflects whether the timeline is actually showing, not just
				// the toggle preference — otherwise it stays lit after the
				// selection drops to none and the timeline disappears.
				active: showTimeline && !timelineDisabled,
				disabled: timelineDisabled,
				onClick: onToggleTimeline,
			},
			{
				type: "custom" as const,
				icon: "search",
				label: "route_search",
				customComponent: searchComponent,
			},
		],
		[
			statusFilter,
			searchComponent,
			routeOptionsButton,
			showPanel,
			onTogglePanel,
			showTimeline,
			onToggleTimeline,
			timelineDisabled,
			hasIneligibleSelectedRoute,
			selectedRouteIds.length,
		]
	)

	// Memoized table actions
	const tableActions = useMemo(
		() => ({
			edit: (_value: any, row: any) => ({
				onClick: (event: React.MouseEvent) => handleRowPress(row, event),
			}),
			exportData: (_value: any, row: any) => {
				const status = row.original?.status || row.status
				const isAggregation = status === "aggregation"
				const route = row.original || row

				const functions = [
					{
						label: Strings.routes.exportRoute,
						icon: "route",
						onClick: () => handleExportRoute(route, notDeliveredReasons),
						disabled: isAggregation,
					},
				]

				if (route.routeType === "van") {
					functions.push({
						label: Strings.routes.exportLoadSequence,
						icon: "list",
						onClick: () => handleExportLoadSequence(route),
						disabled: isAggregation || !route.gridLoadSequence?.length,
					})
				}

				return {
					disabled: isAggregation,
					functions: functions,
				}
			},
		}),
		[handleRowPress, notDeliveredReasons]
	)

	// Wrapper for onRowPress to pass the event
	const handleRowClick = useCallback(
		(row: any, event?: React.MouseEvent) => {
			handleRowPress(row, event)
		},
		[handleRowPress]
	)

	return (
		<Table
			data={routes}
			columns={getTableColumns(
				routes,
				selectedRouteIds,
				setSelectedRouteIds,
				externalDeliveryActive
			)}
			headerOptions={headerOptions}
			onRowPress={handleRowClick}
			getRowStyle={getRowStyle}
			actions={tableActions}
			stickyActions
			paginated
			paginationApi={{
				total: pagination.total,
				pageIndex: pagination.page,
				pageSize: pagination.pageSize,
				setPage: onPageChange,
			}}
		/>
	)
}

export default RoutesTable

function calculateProgress(route: Route) {
	const deliveryAddresses = route.addresses.filter(
		(addr) =>
			!addr.isDepot &&
			addr.order?.orderId &&
			addr.stopType?.toUpperCase() === "DELIVERY"
	)

	// Collect all orders (main + bundled)
	const allOrders: any[] = []
	deliveryAddresses.forEach((addr) => {
		// Add main order
		allOrders.push(addr.order)

		// Add bundled orders if they exist
		if (addr.order.bundledOrders && Array.isArray(addr.order.bundledOrders)) {
			allOrders.push(...addr.order.bundledOrders)
		}
	})

	const total = allOrders.length

	if (total === 0) {
		return {
			total: 0,
			delivered: 0,
			notDelivered: 0,
			onTransit: 0,
			deliveredPercent: 0,
			notDeliveredPercent: 0,
			onTransitPercent: 0,
		}
	}

	const delivered = allOrders.filter(
		(order) => order.status === "delivered"
	).length
	const notDelivered = allOrders.filter((order) =>
		["not_delivered", "cancelled", "returned"].includes(order.status || "")
	).length
	const onTransit = allOrders.filter((order) =>
		["on_transit"].includes(order.status || "")
	).length

	return {
		total,
		delivered,
		notDelivered,
		onTransit,
		deliveredPercent: (delivered / total) * 100,
		notDeliveredPercent: (notDelivered / total) * 100,
		onTransitPercent: (onTransit / total) * 100,
	}
}

function handleExportRoute(
	route: Route,
	notDeliveredReasons: NotDeliveredReason[]
) {
	// Browser timezone info (computed once for the whole export)
	const offsetMinutes = -new Date().getTimezoneOffset()
	const offsetSign = offsetMinutes >= 0 ? "+" : "-"
	const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
		.toString()
		.padStart(2, "0")
	const offsetMins = (Math.abs(offsetMinutes) % 60).toString().padStart(2, "0")
	const utcOffset = `UTC${offsetSign}${offsetHours}:${offsetMins}`
	const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone
	const timezoneLabel = `${timezoneName} (${utcOffset})`

	// Helper function to convert milliseconds to time string (HH:MM)
	const msToTime = (ms: number): string => {
		const hours = Math.floor(ms / 3600000)
		const minutes = Math.floor((ms % 3600000) / 60000)
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
	}

	// Helper function to format slot
	const formatSlot = (slot?: { from: number; to: number }): string => {
		if (!slot) return "-"
		return `${msToTime(slot.from)}-${msToTime(slot.to)}`
	}

	// Helper function to format a unix timestamp as HH:MM in browser local timezone
	const formatTime = (timestamp: number | undefined): string => {
		if (!timestamp) return "-"
		const date = new Date(timestamp * 1000)
		const hours = date.getHours().toString().padStart(2, "0")
		const minutes = date.getMinutes().toString().padStart(2, "0")
		return `${hours}:${minutes}`
	}

	// Helper function to format a date string as locale datetime in browser local timezone and format
	const formatDateTime = (value: string | undefined): string => {
		if (!value) return "-"
		return new Date(value).toLocaleString()
	}

	// Prepare CSV headers
	const headers = [
		Strings.routes.stopNumber,
		Strings.orders.orderId,
		Strings.orders.status,
		Strings.orders.serviceType,
		Strings.orders.clientName,
		Strings.fields.phone,
		Strings.fields.address,
		Strings.routes.eta,
		Strings.routes.etd,
		Strings.routes.slot,
		Strings.orders.boxes,
		Strings.orders.bagId,
		Strings.routes.arrivalTime,
		Strings.routes.departureTime,
		Strings.orders.deliveredDate,
		Strings.orders.notDeliveredReason,
		Strings.orders.notes,
		Strings.routes.stopType,
		Strings.routes.depot,
		Strings.orders.latitude,
		Strings.orders.longitude,
		"Timezone",
	]

	// Prepare CSV rows
	const rows = route.addresses.map((addr, index) => {
		return [
			(index + 1).toString(),
			addr.order?.orderId || "-",
			addr.order?.status ? getStatusLabel(addr.order.status as any) : "-",
			addr.serviceType || addr.order?.serviceType || "-",
			addr.order?.user?.name || "-",
			addr.order?.user?.phone || "-",
			addr.address || "-",
			formatTime(addr.eta),
			formatTime(addr.etd),
			formatSlot(addr.order?.slot),
			addr.order?.boxes
				? `${addr.order.boxes.map((box) => box.id).join("; ")}`
				: "-",
			addr.order?.bagId || "-",
			formatDateTime(addr.order?.arrivalTime),
			formatDateTime(addr.order?.departureTime),
			formatDateTime(addr.order?.deliveredDate),
			notDeliveredReasons.find(
				(r: any) => r.id === addr.order.notDeliveredReason
			)?.reason || addr.order.notDeliveredReason,
			addr.order?.notes || "-",
			addr.stopType || addr.address_stop_type || "-",
			addr.isDepot ? "Yes" : "No",
			addr.location?.lat?.toString() || "-",
			addr.location?.lng?.toString() || "-",
			timezoneLabel,
		]
	})

	// Create CSV content
	const csvContent = [
		headers.join(","),
		...rows.map((row) =>
			row.map((cell) => `"${(cell || "").replace(/"/g, '""')}"`).join(",")
		),
	].join("\n")

	// Create and download file
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
	const link = document.createElement("a")
	const url = URL.createObjectURL(blob)
	link.setAttribute("href", url)
	link.setAttribute("download", `${route.name}_route_details.csv`)
	link.style.visibility = "hidden"
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)

	notification.success({
		message: Strings.routes.exportRoute,
		description: Strings.formatString(
			Strings.routes.exportRouteSuccess,
			route.name
		),
	})
}

function handleExportLoadSequence(route: Route) {
	if (!route.gridLoadSequence || route.gridLoadSequence.length === 0) {
		console.warn("No grid load sequence data to export")
		notification.warning({
			message: Strings.routes.exportLoadSequence,
			description: Strings.routes.noLoadSequenceData,
		})
		return
	}

	// Determine load sequence type based on available fields
	const firstItem = route.gridLoadSequence[0]
	let loadSequenceType: "grid" | "pile" | "line" = "grid"

	if (firstItem.pile !== undefined || firstItem.position !== undefined) {
		loadSequenceType = "pile"
	} else if (firstItem.line !== undefined && firstItem.column === undefined) {
		loadSequenceType = "line"
	}

	// Define CSV headers based on load sequence type
	let headers: string[] = []
	let rows: string[][] = []

	if (loadSequenceType === "grid") {
		headers = [
			Strings.orders.boxId,
			Strings.orders.line,
			Strings.orders.column,
			Strings.orders.depth,
			Strings.fields.weight,
		]
		rows = route.gridLoadSequence.map((item) => [
			item.boxId || "-",
			(item.line || 0).toString(),
			(item.column || 0).toString(),
			(item.depth || 0).toString(),
			`${item.boxWeight || 0} kg`,
		])
	} else if (loadSequenceType === "pile") {
		headers = [Strings.orders.position, Strings.orders.pile]
		rows = route.gridLoadSequence.map((item) => [
			(item.position || "-").toString(),
			(item.pile || "-").toString(),
		])
	} else {
		// line type
		headers = [
			Strings.orders.boxId,
			Strings.orders.position,
			Strings.fields.weight,
		]
		rows = route.gridLoadSequence.map((item) => [
			item.boxId || "-",
			(item.line || 0).toString(),
			`${item.boxWeight || 0} kg`,
		])
	}

	// Add out of format boxes if they exist
	const outOfFormatBoxes = route.boxesOutOfFormat || []
	if (outOfFormatBoxes.length > 0) {
		// Add empty row separator
		rows.push(Array(headers.length).fill(""))

		// Add "Out of Format Boxes" header row
		const outOfFormatHeaderRow = Array(headers.length).fill("")
		outOfFormatHeaderRow[0] = "Out of Format Boxes"
		rows.push(outOfFormatHeaderRow)

		// Add out of format box rows
		outOfFormatBoxes.forEach((box) => {
			const outOfFormatRow = Array(headers.length).fill("")
			outOfFormatRow[0] = box.boxId
			rows.push(outOfFormatRow)
		})
	}

	// Create CSV content
	const csvContent = [
		headers.join(","),
		...rows.map((row) =>
			row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
		),
	].join("\n")

	// Create and download file
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
	const link = document.createElement("a")
	const url = URL.createObjectURL(blob)
	link.setAttribute("href", url)
	link.setAttribute("download", `${route.name}_load_sequence.csv`)
	link.style.visibility = "hidden"
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

// Picks the message in the active language, falling back to en → pt → code.
const localizeMessage = (
	message?: { [lang: string]: string | undefined } | null
): string => {
	if (!message) return ""
	const lang = Strings.getLanguage?.()
	return (lang && message[lang]) || message.en || message.pt || ""
}

// A filled status icon at the column's standard size.
const statusIcon = (Icon: typeof CheckCircleFilled, color: string) => (
	<Icon style={{ fontSize: 18, color, display: "block" }} />
)

// A coloured badge showing the warning-order count (replaces the "!" icon).
const countBadge = (count: number, color: string) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			minWidth: 20,
			height: 20,
			padding: "0 5px",
			borderRadius: 10,
			background: color,
			color: "#fff",
			fontSize: 12,
			fontWeight: 600,
			lineHeight: 1,
		}}
	>
		{count}
	</span>
)

const pickupBadge = (label: React.ReactNode) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 20,
			height: 20,
			borderRadius: "50%",
			border: "1.5px solid #818181ff",
			color: "#4d4d4dff",
			fontSize: 12,
			fontWeight: 600,
			lineHeight: 1,
		}}
	>
		{label}
	</span>
)

// Maps an external-eligibility status to how it renders and the fallback tooltip
// (shown when the backend sends no reason message). `fallback` is a getter so it
// resolves in the active language at render time.
const EXTERNAL_STATUS_STYLE: Record<
	ExternalEligibilityStatus,
	{
		render: (e: RouteExternalEligibility) => React.ReactNode
		fallback: () => string
	}
> = {
	eligible: {
		render: () => statusIcon(CheckCircleFilled, "#52c41a"),
		fallback: () => Strings.routes.externalEligibleTooltip,
	},
	warning: {
		render: (e) => countBadge(e.counts?.warning ?? 0, "#faad14"),
		fallback: () => Strings.routes.externalWarningTooltip,
	},
	ineligible: {
		render: () => statusIcon(CloseCircleFilled, "#ff4d4f"),
		fallback: () => Strings.routes.externalNotEligibleTooltip,
	},
	assigned: {
		render: () => statusIcon(MinusCircleFilled, "#bfbfbf"),
		fallback: () => Strings.routes.externalAssignedTooltip,
	},
}

const getTableColumns = (
	routes: any[],
	selectedRouteIds: string[],
	setSelectedRoutes: (ids: string[]) => void,
	externalDeliveryActive: boolean
) => [
	{
		id: "selection",
		Header: () => {
			const allSelected =
				selectedRouteIds.length > 0 && selectedRouteIds.length === routes.length

			const toggleSelectAll = () => {
				const allSelected =
					selectedRouteIds.length > 0 &&
					selectedRouteIds.length === routes.length
				if (allSelected) {
					setSelectedRoutes([])
				} else {
					setSelectedRoutes(routes.map((route: { _id: string }) => route._id))
				}
			}

			return (
				<Tooltip title={Strings.routes.multiSelectHint}>
					<span
						className="RoutesTable__select-all-checkbox"
						onClick={toggleSelectAll}
					>
						<Checkbox
							checked={allSelected}
							indeterminate={selectedRouteIds.length > 0 && !allSelected}
						/>
					</span>
				</Tooltip>
			)
		},
		Cell: ({ row }: any) => {
			return renderVehicleTypeIcon(row.original?.routeType || row.routeType)
		},
		maxWidth: 35,
		sortable: false,
	},
	{
		Header: Strings.routes.routeName,
		id: "route_name",
		accessor: "name",
		Cell: ({ row }: any) => row.original?.name || row.name || "-",
	},
	{
		Header: Strings.orders.status,
		id: "route_status",
		accessor: "status",
		Cell: ({ row }: any) => {
			const status = row.original?.status || row.status
			return <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
		},
	},
	{
		Header: Strings.routes.orders,
		id: "route_orders",
		accessor: "addresses",
		Cell: ({ row }: any) => {
			const progress = calculateProgress(row.original || row)

			const formattedText = `${progress.delivered + progress.notDelivered}/${progress.total}`
			const totalCompleted =
				progress.deliveredPercent + progress.notDeliveredPercent

			return (
				<Progress
					style={{ width: "90%" }}
					strokeColor="var(--error-color)"
					percent={totalCompleted}
					success={{
						percent: progress.deliveredPercent,
						strokeColor: "#52c41a",
					}}
					format={() => <span style={{ color: "#000" }}>{formattedText}</span>}
				/>
			)
		},
		minWidth: 150,
	},
	{
		Header: Strings.fields.date,
		id: "route_date",
		accessor: "date",
		Cell: ({ row }: any) => {
			const date = row.original?.date || row.date
			return date ? new Date(date).toLocaleDateString() : "-"
		},
	},
	{
		Header: Strings.orders.driver,
		id: "route_driver",
		accessor: "driver",
		Cell: ({ row }: any) => {
			const route: Route = row.original || row

			// If there's an external delivery, show the partner text instead of internal driver
			const ext = route.externalDelivery
			if (ext) {
				let label

				// If we have courier info, show it with name and vehicle, otherwise fallback to partner name and batching status
				const partner = ext.partner
					? ext.partner.charAt(0).toUpperCase() + ext.partner.slice(1)
					: "-"
				const courier = route.addresses.find((addr) => !addr.isDepot)?.order
					?.externalDelivery?.courier
				if (courier) {
					label = `${partner} · ${courier?.name}`
				} else {
					const batchingLabel: Record<string, string> = {
						waiting_for_batching: Strings.routes.externalBatchingProcessing,
						batched: Strings.routes.externalBatchingSearching,
						in_progress: Strings.routes.externalBatchingInProgress,
						completed: Strings.routes.externalBatchingCompleted,
					}
					const statusOrder = [
						"waiting_for_batching",
						"batched",
						"in_progress",
						"completed",
					]
					const overallStatus = (ext.batches || []).reduce<string | undefined>(
						(min, b) => {
							if (!b.status) return min
							if (!min) return b.status
							return statusOrder.indexOf(b.status) < statusOrder.indexOf(min)
								? b.status
								: min
						},
						undefined
					)
					const subLabel = overallStatus ? batchingLabel[overallStatus] : null
					label = subLabel ? `${partner} · ${subLabel}` : partner
				}

				return (
					<span style={{ color: "#d46b08", fontStyle: "italic" }}>{label}</span>
				)
			}

			return route.driverInfo?.name || "-"
		},
	},
	{
		Header: Strings.orders.vehicle,
		id: "route_vehicle",
		accessor: "vehicle",
		Cell: ({ row }: any) => {
			const route: Route = row.original || row

			// If there's an external delivery with courier vehicle info, show that instead of internal vehicle
			const ext = route.externalDelivery
			const partner = ext?.partner
				? ext.partner?.charAt(0).toUpperCase() + ext.partner?.slice(1)
				: "-"
			const courier = route.addresses.find((addr) => !addr.isDepot)?.order
				?.externalDelivery?.courier
			if (partner && courier) {
				const vehicleInfo =
					`${courier.vehicleMake || ""} ${courier.vehicleModel || ""} ${courier.vehicleColor || ""}`.trim()
				return (
					<span style={{ color: "#d46b08", fontStyle: "italic" }}>
						{partner} · {vehicleInfo}
					</span>
				)
			}

			// Show "Vehicle Name - License Plate" if both exist, otherwise show whichever is available
			const vehicle = route.vehicleInfo
			const vehicleName = vehicle?.name
			const vehicleLicensePlate = vehicle?.licensePlate

			if (vehicleName && vehicleLicensePlate) {
				return `${vehicleName} - ${vehicleLicensePlate}`
			}
			return vehicleName || vehicleLicensePlate || "-"
		},
	},
	{
		Header: Strings.routes.pickupColumn,
		id: "route_pickup",
		accessor: "pickup",
		sortable: false,
		align: "center" as const,
		maxWidth: 80,
		Cell: ({ row }: any) => {
			const route: Route = row.original || row
			const pickup = route.pickup
			if (!pickup) return "-"

			// Van pickup
			if (pickup.type === "van") {
				let sentence: React.ReactNode = null
				if (pickup.sequenceNr != null) {
					sentence = pickup.vanRouteName
						? Strings.formatString(
								Strings.routes.pickupVanTooltip,
								String(pickup.sequenceNr),
								pickup.vanRouteName
							)
						: Strings.formatString(
								Strings.routes.pickupVanTooltipNoName,
								String(pickup.sequenceNr)
							)
				}

				const tooltip = (
					<div>
						{sentence ? (
							<div style={{ fontWeight: 600 }}>{sentence}</div>
						) : (
							pickup.vanRouteName && (
								<div style={{ fontWeight: 600 }}>{pickup.vanRouteName}</div>
							)
						)}
						{pickup.address && (
							<div
								style={{
									marginTop: sentence || pickup.vanRouteName ? 4 : 0,
								}}
							>
								{pickup.address}
							</div>
						)}
					</div>
				)
				return (
					<Tooltip title={tooltip}>
						{pickupBadge(pickup.sequenceNr ?? "-")}
					</Tooltip>
				)
			}

			// Warehouse pickup
			const tooltip = (
				<div>
					<div style={{ fontWeight: 600 }}>
						{Strings.routes.pickupWarehouse}
					</div>
					{pickup.address && (
						<div style={{ marginTop: 4 }}>{pickup.address}</div>
					)}
				</div>
			)
			return (
				<Tooltip title={tooltip}>
					<span style={{ display: "inline-flex" }}>
						<WarehouseIcon
							style={{ width: 18, height: 18, color: "#818181ff" }}
						/>
					</span>
				</Tooltip>
			)
		},
	},
	// External-delivery eligibility, red or green icon with tooltip, only if external delivery is active and at least one route is dispatching
	...(externalDeliveryActive &&
	routes.some((route: Route) => route.status === "dispatching")
		? [
				{
					Header: Strings.routes.externalEligibilityColumn,
					id: "route_external_eligibility",
					accessor: "externalEligibility",
					sortable: false,
					align: "center" as const,
					maxWidth: 90,
					Cell: ({ row }: any) => {
						const route: Route = row.original || row
						const eligibility = route.externalEligibility
						if (!eligibility) return "-"

						const { render, fallback } =
							EXTERNAL_STATUS_STYLE[eligibility.status] ??
							EXTERNAL_STATUS_STYLE.ineligible

						const message =
							localizeMessage(eligibility.reason?.message) || fallback()
						// List the flagged orders ("orderId - client name") so the
						// operator knows exactly which ones need attention.
						const warningOrders = eligibility.warningOrders ?? []
						const tooltip = warningOrders.length ? (
							<div>
								<div>{message}</div>
								<div style={{ marginTop: 8 }}>
									{warningOrders.map((o) => (
										<div key={o.orderId}>
											{o.orderId}
											{o.name ? ` - ${o.name}` : ""}
										</div>
									))}
								</div>
							</div>
						) : (
							message
						)

						return <Tooltip title={tooltip}>{render(eligibility)}</Tooltip>
					},
				},
			]
		: []),
]
